import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { FileUploadFile } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StudentProfile } from '../../../../api/types/settings/settings-all';
import { AssignmentWithStudentSubmissionWithCourseDetails } from '../../../../api/types/students/assignments/student-assignments';
import { CourseAssignmentStatus } from '../../../../api/types/programs/course/assignments/course-assignment-status';
import { UploadFile } from '../../../../pages/user-page/pages/docs/componenets/upload-file/upload-file';
import { studentAssignmentUploadOperation } from './operations/student-assignment-upload-operation';
import { studentAssignmentDeleteOperation } from './operations/student-assignment-delete-operation';
import { toast } from 'react-toastify';
import BoubleText, {
  BubbleType,
} from '../../../../component/bubble-text/bouble-text';

export const MyAssignments = observer(() => {
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] =
    useState<AssignmentWithStudentSubmissionWithCourseDetails | null>(null);
  const [uploadedFile, setUploadedFile] = useState<FileUploadFile>();
  const [deleteFileLoading, setDeleteFileLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    courseName: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    assignmentStatus: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
    submissionStatus: {
      value: null,
      matchMode: FilterMatchMode.EQUALS,
    },
  });

  // Check if user profile exists and is a student
  if (!globalUserProfile.userProfile?.profile) {
    return <div>Loading user profile...</div>;
  }

  const studentProfile = globalUserProfile.userProfile
    .profile as StudentProfile;
  const rawAssignments: AssignmentWithStudentSubmissionWithCourseDetails[] =
    studentProfile.assignments;

  // Helper function to check if assignment is open for submission
  const isOpenForSubmit = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    const now = new Date();

    // Get the effective due date (custom if exists, otherwise regular)
    const effectiveDueDate = assignment.customDueDate || assignment.dueDate;

    // Convert to Date object if it's a string
    const dueDate = new Date(effectiveDueDate);

    return dueDate > now;
  };

  // Add computed fields for filtering
  const assignments = rawAssignments.map((assignment) => ({
    ...assignment,
    assignmentStatus: isOpenForSubmit(assignment) ? 'open' : 'closed',
    submissionStatus: assignment.submittedAt ? 'submitted' : 'not_submitted',
  }));

  if (!assignments || assignments.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>מטלות להגשה</h2>
        <p>לא נמצאו מטלות רשומות</p>
      </div>
    );
  }

  // Handler functions
  const openUploadDialog = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    setSelectedAssignment(assignment);
    setUploadDialogVisible(true);
    setUploadedFile(undefined);
  };

  const closeUploadDialog = () => {
    setUploadDialogVisible(false);
    setSelectedAssignment(null);
    setUploadedFile(undefined);
  };

  const handleUpload = async () => {
    if (!selectedAssignment || !uploadedFile) {
      return;
    }

    await studentAssignmentUploadOperation(
      selectedAssignment.programId,
      selectedAssignment.courseId,
      selectedAssignment.id,
      uploadedFile,
      closeUploadDialog,
      setLoading,
    );
  };

  const handleDeleteFile = async (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    await studentAssignmentDeleteOperation(
      assignment.programId,
      assignment.courseId,
      assignment.id,
      setDeleteFileLoading,
    );
  };

  // Template functions for DataTable columns
  const statusTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (isOpenForSubmit(assignment)) {
      return <BoubleText text={'פתוח להגשה'} type={BubbleType.GREEN} />;
    } else {
      return <BoubleText text={'סגור להגשה'} type={BubbleType.RED} />;
    }
    switch (assignment.status) {
      case CourseAssignmentStatus.OPEN_TO_SUBMIT:
        return (
          <Tag
            style={{ width: '60px' }}
            value="פתוח להגשה"
            severity="success"
          />
        );
      case CourseAssignmentStatus.CLOSE_TO_SUBMIT:
        return (
          <Tag style={{ width: '60px' }} value="סגור להגשה" severity="danger" />
        );
      default:
        return (
          <Tag
            style={{ width: '60px' }}
            className={'neutral-tag'}
            value="לא ידוע"
            severity="secondary"
          />
        );
    }
  };

  const gradeTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (assignment.grade !== undefined && assignment.grade !== null) {
      return assignment.grade;
    }
    return assignment.isGradesPublished ? 'לא הוגש' : 'טרם פורסם';
  };

  const weightTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    return `${assignment.assignmentWeight}%`;
  };

  const dueDateTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    const dueDate = assignment.customDueDate || assignment.dueDate;
    return new Date(dueDate).toLocaleDateString('he-IL');
  };

  const submissionStatusTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    if (assignment.studentFileUrl) {
      return (
        <Tag
          value={`הוגש ב-${new Date(assignment.submittedAt).toLocaleDateString('he-IL')}`}
          severity="success"
        />
      );
    }
    return <Tag value="לא הוגש" severity="warning" />;
  };

  const fileTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    const isOpenForSubmit2 =
      assignment.status === CourseAssignmentStatus.OPEN_TO_SUBMIT;
    const hasFile =
      assignment.studentFileUrl && assignment.studentFileUrl.length > 0;

    if (hasFile) {
      // Show download, upload and delete buttons when file exists (like admin interface)
      return (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            label="הורד קובץ"
            icon="pi pi-download"
            size="small"
            text
            onClick={() => window.open(assignment.studentFileUrl, '_blank')}
          />
          <Button
            icon="pi pi-trash"
            size="small"
            text
            tooltip={'מחיקת הקובץ'}
            severity="danger"
            loading={deleteFileLoading}
            onClick={() => handleDeleteFile(assignment)}
          />

          {isOpenForSubmit2 && (
            <Button
              label="החלף קובץ"
              icon="pi pi-upload"
              size="small"
              text
              severity="info"
              onClick={() => openUploadDialog(assignment)}
            />
          )}
        </div>
      );
    } else if (isOpenForSubmit(assignment)) {
      // Show upload link when no file (like the admin interface)
      return (
        <label
          onClick={() => openUploadDialog(assignment)}
          style={{
            whiteSpace: 'nowrap',
            color: '#0066cc',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = '#e0e0e0')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          לחץ להגשה
        </label>
      );
    } else {
      return <label>לא ניתן להגיש את המטלה</label>;
    }
  };

  const submittedByTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    return assignment.submittedByName || '-';
  };

  const gradesPublishedTemplate = (
    assignment: AssignmentWithStudentSubmissionWithCourseDetails,
  ) => {
    return assignment.isGradesPublished ? 'כן' : 'לא';
  };

  // Filter elements
  const statusFilterElement = (options: ColumnFilterElementTemplateOptions) => {
    const statusOptions = [
      { label: 'פתוח להגשה', value: 'open' },
      { label: 'סגור להגשה', value: 'closed' },
    ];

    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder="בחר סטטוס"
        className="p-column-filter"
        style={{
          width: '100%',
          height: '30px',
          alignItems: 'center',
        }}
      />
    );
  };

  const submissionStatusFilterElement = (
    options: ColumnFilterElementTemplateOptions,
  ) => {
    const submissionOptions = [
      { label: 'הוגש', value: 'submitted' },
      { label: 'לא הוגש', value: 'not_submitted' },
    ];

    return (
      <Dropdown
        value={options.value}
        options={submissionOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        optionLabel="label"
        optionValue="value"
        placeholder="בחר סטטוס"
        className="p-column-filter"
        style={{
          width: '100%',
          height: '30px',
          alignItems: 'center',
        }}
      />
    );
  };

  return (
    <div>
      <Dialog
        header="העלאת קובץ למטלה"
        visible={uploadDialogVisible}
        style={{
          width: '40vw',
          backgroundColor: 'white',
          padding: '8px',
          borderRadius: '8px',
        }}
        onHide={closeUploadDialog}
      >
        <UploadFile onFileUploaded={setUploadedFile} />
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <Button
            style={{ height: '35px', marginTop: '16px' }}
            label={'העלאת קובץ'}
            onClick={handleUpload}
            disabled={!uploadedFile}
            loading={loading}
          />
        </div>
      </Dialog>

      <h2>מטלות</h2>
      <DataTable
        value={assignments}
        stripedRows
        showGridlines
        tableStyle={{ minWidth: '50rem' }}
        rows={10}
        style={{ marginTop: '16px' }}
        filterDisplay="row"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
      >
        <Column
          field="assignmentName"
          header="שם המטלה"
          style={{ textAlign: 'right', fontWeight: 'bold' }}
        />
        <Column
          field="courseName"
          header="קורס"
          style={{ textAlign: 'right' }}
          filter
          filterPlaceholder="חפש קורס"
          showFilterMenu={false}
        />
        {/*        <Column
          field="programName"
          header="תוכנית לימודים"
          style={{ textAlign: 'right' }}
        />*/}

        <Column
          header="תאריך אחרון להגשה"
          body={dueDateTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סטטוס"
          body={statusTemplate}
          style={{ textAlign: 'right' }}
          filter
          showFilterMenu={false}
          filterField="assignmentStatus"
          filterElement={statusFilterElement}
        />
        <Column
          header="משקל"
          body={weightTemplate}
          style={{ textAlign: 'right' }}
        />

        <Column
          header="קובץ המטלה"
          body={(rowData) => {
            return rowData.fileUrl ? (
              <Button
                label="הורד קובץ"
                icon="pi pi-download"
                size="small"
                text
                onClick={() => window.open(rowData.fileUrl, '_blank')}
              />
            ) : (
              'לא הועלה מסמך דרישות'
            );
          }}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="סטטוס הגשה"
          body={submissionStatusTemplate}
          style={{ textAlign: 'right' }}
          filter
          showFilterMenu={false}
          filterField="submissionStatus"
          filterElement={submissionStatusFilterElement}
        />
        <Column
          header="ציון"
          body={gradeTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="ציונים פורסמו"
          body={gradesPublishedTemplate}
          style={{ textAlign: 'right' }}
        />
        <Column
          header="קובץ"
          body={fileTemplate}
          style={{ textAlign: 'right' }}
        />
        {/*<Column
          header="הוגש על ידי"
          body={submittedByTemplate}
          style={{ textAlign: 'right' }}
        />*/}
      </DataTable>
    </div>
  );
});
